/**
 * Componente oficial de identidade visual LEGISCITY com suporte dinâmico a Dark Mode.
 *
 * @module components/BrandLogo
 */

import { memo } from "react";

/**
 * Logotipo oficial LEGISCITY — O Legislativo no Território.
 *
 * @param {object} props
 * @param {"horizontal" | "icon" | "full" | "compact"} [props.variant="horizontal"]
 * @param {number|string} [props.height=36] - Altura em pixels ou CSS
 * @param {boolean} [props.isDark] - Se ativo, adapta as cores escuras para contraste ideal em fundos escuros
 * @param {string} [props.className]
 * @param {object} [props.style]
 * @param {boolean} [props.showSubtitle=true] - Exibir subtítulo na versão horizontal
 */
function BrandLogoComponent({
  variant = "horizontal",
  height = 36,
  isDark = false,
  className = "",
  style = {},
  showSubtitle = true,
}) {
  const detectedDark = isDark || (typeof document !== "undefined" && document.documentElement?.getAttribute("data-theme") === "dark");
  const primaryTextColor = detectedDark ? "#ffffff" : "#042B5B";
  const subtitleColor = detectedDark ? "rgba(255, 255, 255, 0.75)" : "rgba(4, 43, 91, 0.85)";

  if (variant === "icon") {
    return (
      <svg
        viewBox="0 0 100 100"
        height={height}
        width={typeof height === "number" ? height : "auto"}
        className={className}
        style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }}
        aria-label="LEGISCITY Pin"
      >
        {/* PIN SÓLIDO COM CONTORNO BRANCO */}
        <path
          d="M 50 6 C 31 6 16 21 16 40 C 16 63 47 88.5 49 90 C 49.6 90.5 50.4 90.5 51 90 C 53 88.5 84 63 84 40 C 84 21 69 6 50 6 Z"
          fill="#042B5B"
          stroke="#FFFFFF"
          strokeWidth="3"
          strokeLinejoin="round"
        />

        {/* CÍRCULO INTERNO EM BRANCO SÓLIDO */}
        <circle cx="50" cy="39" r="25.5" fill="#FFFFFF" />

        {/* FÓRUM COM TETO/BASE ARREDONDADOS E COLUNAS NAS CORES DO MAPA */}
        <g transform="translate(30.5, 18.5) scale(0.81)">
          {/* Frontão / Teto Arredondado em Azul Marinho */}
          <path
            d="M 43.5 12.2 L 25.5 3.5 C 24.5 3 23.5 3 22.5 3.5 L 4.5 12.2 C 3.2 12.8 2.5 14 2.5 15.3 C 2.5 16.8 3.7 17.8 5.2 17.8 L 42.8 17.8 C 44.3 17.8 45.5 16.8 45.5 15.3 C 45.5 14 44.8 12.8 43.5 12.2 Z"
            fill="#042B5B"
          />

          {/* 3 Colunas nas Cores do Mapa com cantos arredondados */}
          <rect x="8.5" y="20" width="6.5" height="14" rx="1.5" fill="#20AE5B" />
          <rect x="20.75" y="20" width="6.5" height="14" rx="1.5" fill="#0C72F0" />
          <rect x="33" y="20" width="6.5" height="14" rx="1.5" fill="#FDC224" />

          {/* Base superior arredondada */}
          <rect x="5.5" y="36" width="37" height="4" rx="1.5" fill="#042B5B" />

          {/* Base inferior arredondada */}
          <rect x="3" y="42" width="42" height="4.5" rx="2" fill="#042B5B" />
        </g>
      </svg>
    );
  }

  if (variant === "full") {
    return (
      <svg
        viewBox="0 0 120 120"
        height={height}
        width={typeof height === "number" ? height : "auto"}
        className={className}
        style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }}
        aria-label="LEGISCITY Logo Principal"
      >
        {/* FUNDO BRANCO EM VOLTA DO MAPA EMBAIXO */}
        <g transform="translate(18, 58) scale(1.5, 0.95)">
          <path
            d="M 7.37 50.71 C 7.98 50.71 8.57 50.52 9.34 50.10 L 19.19 44.80 L 19.19 5.01 C 18.76 5.22 18.32 5.45 17.87 5.71 L 6.76 12.06 C 5.36 12.86 4.70 13.98 4.70 15.55 L 4.70 48.02 C 4.70 49.75 5.71 50.71 7.37 50.71 Z"
            fill="#FFFFFF"
            stroke="#FFFFFF"
            strokeWidth="4"
            strokeLinejoin="round"
          />
          <path
            d="M 34.19 51.41 L 34.19 12.20 L 22.61 5.13 C 22.21 4.87 21.74 4.70 21.30 4.59 L 21.30 44.34 L 33.11 50.95 C 33.48 51.16 33.84 51.30 34.19 51.41 Z"
            fill="#FFFFFF"
            stroke="#FFFFFF"
            strokeWidth="4"
            strokeLinejoin="round"
          />
          <path
            d="M 36.30 51.37 C 36.53 51.30 36.77 51.20 37.00 51.04 L 49.26 44.08 C 50.64 43.30 51.30 42.18 51.30 40.61 L 51.30 8.10 C 51.30 6.37 50.31 5.43 48.63 5.43 C 48.04 5.43 47.43 5.59 46.68 6.02 L 36.30 11.78 Z"
            fill="#FFFFFF"
            stroke="#FFFFFF"
            strokeWidth="4"
            strokeLinejoin="round"
          />
        </g>

        {/* MAPA DOBRADO NA BASE */}
        <g transform="translate(18, 58) scale(1.5, 0.95)">
          <path
            d="M 7.37 50.71 C 7.98 50.71 8.57 50.52 9.34 50.10 L 19.19 44.80 L 19.19 5.01 C 18.76 5.22 18.32 5.45 17.87 5.71 L 6.76 12.06 C 5.36 12.86 4.70 13.98 4.70 15.55 L 4.70 48.02 C 4.70 49.75 5.71 50.71 7.37 50.71 Z"
            fill="#20AE5B"
            stroke="#FFFFFF"
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
          <path
            d="M 34.19 51.41 L 34.19 12.20 L 22.61 5.13 C 22.21 4.87 21.74 4.70 21.30 4.59 L 21.30 44.34 L 33.11 50.95 C 33.48 51.16 33.84 51.30 34.19 51.41 Z"
            fill="#0C72F0"
            stroke="#FFFFFF"
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
          <path
            d="M 36.30 51.37 C 36.53 51.30 36.77 51.20 37.00 51.04 L 49.26 44.08 C 50.64 43.30 51.30 42.18 51.30 40.61 L 51.30 8.10 C 51.30 6.37 50.31 5.43 48.63 5.43 C 48.04 5.43 47.43 5.59 46.68 6.02 L 36.30 11.78 Z"
            fill="#FDC224"
            stroke="#FFFFFF"
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
        </g>

        {/* PIN COM O TRIBUNAL */}
        <g>
          <path
            d="M 60 12 C 44 12 31 24.5 31 40.5 C 31 58.5 56.5 82.5 58.8 84.7 C 59.5 85.3 60.5 85.3 61.2 84.7 C 63.5 82.5 89 58.5 89 40.5 C 89 24.5 76 12 60 12 Z"
            fill="#042B5B"
            stroke="#FFFFFF"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          <circle cx="60" cy="39.5" r="21" fill="#FFFFFF" />
          <g transform="translate(44.5, 23.5) scale(0.64)">
            <path
              d="M 43.5 12.2 L 25.5 3.5 C 24.5 3 23.5 3 22.5 3.5 L 4.5 12.2 C 3.2 12.8 2.5 14 2.5 15.3 C 2.5 16.8 3.7 17.8 5.2 17.8 L 42.8 17.8 C 44.3 17.8 45.5 16.8 45.5 15.3 C 45.5 14 44.8 12.8 43.5 12.2 Z"
              fill="#042B5B"
            />
            <rect x="9" y="20" width="6" height="14" rx="1.5" fill="#042B5B" />
            <rect x="21" y="20" width="6" height="14" rx="1.5" fill="#042B5B" />
            <rect x="33" y="20" width="6" height="14" rx="1.5" fill="#042B5B" />
            <rect x="5.5" y="36" width="37" height="4" rx="1.5" fill="#042B5B" />
            <rect x="3" y="42" width="42" height="4.5" rx="2" fill="#042B5B" />
          </g>
        </g>
      </svg>
    );
  }

  // Padrão: "horizontal" ou "compact" (Lockup com Ícone + Tipografia LEGISCITY + Subtítulo opcional)
  const hasSubtitle = variant === "compact" ? false : showSubtitle;
  const vbWidth = hasSubtitle ? 216 : 196;
  const vbHeight = 48;
  const calculatedWidth = typeof height === "number" ? Math.round(height * (vbWidth / vbHeight)) : "auto";

  return (
    <svg
      viewBox={`0 0 ${vbWidth} ${vbHeight}`}
      height={height}
      width={calculatedWidth}
      className={className}
      style={{
        display: "inline-block",
        verticalAlign: "middle",
        flexShrink: 0,
        overflow: "visible",
        ...style,
      }}
      aria-label="LEGISCITY — O Legislativo no Território"
    >
      {/* ÍCONE À ESQUERDA */}
      <g transform="translate(4, 2) scale(0.46)">
        {/* Contorno Branco */}
        <g
          transform="matrix(1.28 0 0 .82 14.16 46)"
          fill="#FFFFFF"
          stroke="#FFFFFF"
          strokeWidth="4.2"
          strokeLinejoin="round"
        >
          <path d="M 7.37 50.71 C 7.98 50.71 8.57 50.52 9.34 50.10 L 19.19 44.80 L 19.19 5.01 C 18.76 5.22 18.32 5.45 17.87 5.71 L 6.76 12.06 C 5.36 12.86 4.70 13.98 4.70 15.55 L 4.70 48.02 C 4.70 49.75 5.71 50.71 7.37 50.71 Z" />
          <path d="M 34.19 51.41 L 34.19 12.20 L 22.61 5.13 C 22.21 4.87 21.74 4.70 21.30 4.59 L 21.30 44.34 L 33.11 50.95 C 33.48 51.16 33.84 51.30 34.19 51.41 Z" />
          <path d="M 36.30 51.37 C 36.53 51.30 36.77 51.20 37.00 51.04 L 49.26 44.08 C 50.64 43.30 51.30 42.18 51.30 40.61 L 51.30 8.10 C 51.30 6.37 50.31 5.43 48.63 5.43 C 48.04 5.43 47.43 5.59 46.68 6.02 L 36.30 11.78 Z" />
        </g>
        {/* Mapa */}
        <g transform="matrix(1.28 0 0 .82 14.16 46)">
          <path
            d="M 7.37 50.71 C 7.98 50.71 8.57 50.52 9.34 50.10 L 19.19 44.80 L 19.19 5.01 C 18.76 5.22 18.32 5.45 17.87 5.71 L 6.76 12.06 C 5.36 12.86 4.70 13.98 4.70 15.55 L 4.70 48.02 C 4.70 49.75 5.71 50.71 7.37 50.71 Z"
            fill="#20AE5B"
            stroke="#FFFFFF"
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
          <path
            d="M 34.19 51.41 L 34.19 12.20 L 22.61 5.13 C 22.21 4.87 21.74 4.70 21.30 4.59 L 21.30 44.34 L 33.11 50.95 C 33.48 51.16 33.84 51.30 34.19 51.41 Z"
            fill="#0C72F0"
            stroke="#FFFFFF"
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
          <path
            d="M 36.30 51.37 C 36.53 51.30 36.77 51.20 37.00 51.04 L 49.26 44.08 C 50.64 43.30 51.30 42.18 51.30 40.61 L 51.30 8.10 C 51.30 6.37 50.31 5.43 48.63 5.43 C 48.04 5.43 47.43 5.59 46.68 6.02 L 36.30 11.78 Z"
            fill="#FDC224"
            stroke="#FFFFFF"
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
        </g>
        {/* Pin */}
        <path
          d="M 50 8 C 36 8 25 19 25 33 C 25 49 47 70 49 72 C 49.6 72.6 50.4 72.6 51 72 C 53 70 75 49 75 33 C 75 19 64 8 50 8 Z"
          fill="#042B5B"
          stroke="#FFFFFF"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <circle cx="50" cy="32" r="18" fill="#FFFFFF" />
        {/* Fórum */}
        <g transform="matrix(.54 0 0 .54 37 17)">
          <path
            d="M 43.5 12.2 L 25.5 3.5 C 24.5 3 23.5 3 22.5 3.5 L 4.5 12.2 C 3.2 12.8 2.5 14 2.5 15.3 C 2.5 16.8 3.7 17.8 5.2 17.8 L 42.8 17.8 C 44.3 17.8 45.5 16.8 45.5 15.3 C 45.5 14 44.8 12.8 43.5 12.2 Z"
            fill="#042B5B"
          />
          <rect x="9" y="20" width="6" height="14" rx="1.5" fill="#042B5B" />
          <rect x="21" y="20" width="6" height="14" rx="1.5" fill="#042B5B" />
          <rect x="33" y="20" width="6" height="14" rx="1.5" fill="#042B5B" />
          <rect x="5.5" y="36" width="37" height="4" rx="1.5" fill="#042B5B" />
          <rect x="3" y="42" width="42" height="4.5" rx="2" fill="#042B5B" />
        </g>
      </g>

      {/* TIPOGRAFIA */}
      <g transform={hasSubtitle ? "translate(54, 28)" : "translate(54, 30)"}>
        <text
          fontFamily="Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
          fontWeight="900"
          fontSize={hasSubtitle ? "21" : "22"}
          letterSpacing="0.5"
        >
          <tspan fill={primaryTextColor}>LEGIS</tspan>
          <tspan fill="#0C72F0">CITY</tspan>
        </text>
        {hasSubtitle && (
          <text
            x="0"
            y="12"
            fontFamily="Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
            fontWeight="700"
            fontSize="7"
            letterSpacing="0.8"
            fill={subtitleColor}
          >
            O LEGISLATIVO NO TERRITÓRIO
          </text>
        )}
      </g>
    </svg>
  );
}

export const BrandLogo = memo(BrandLogoComponent);
export default BrandLogo;
